import {
  fieldSchema,
  type Project,
  type DynamicSource,
  type SourceParameters,
  type Values,
} from "./schema";
import { effectiveFields, validateSubmission } from "./engine";
import { parseContext, ContextError } from "./context";

/** Public parameter controls are separate from document inputs and never define host credentials/system values. */
export function dynamicParameterProject(
  project: Project,
  source: DynamicSource,
): Project {
  return {
    ...project,
    dynamicSources: undefined,
    fields: source.parameters.map((parameter, order) =>
      fieldSchema.parse({
        ...parameter,
        default:
          parameter.type === "checkbox" && parameter.default === ""
            ? false
            : typeof parameter.default === "string" &&
                parameter.whitespace === "trim"
              ? parameter.default.trim()
              : parameter.default,
        id: parameter.key,
        order,
        source: { kind: "literal" },
        validation: { maxLength: 1000 },
      }),
    ),
    parts: [],
    forms: [],
    formInstances: [],
    groups: [],
    tabs: [],
    triggers: [],
    pages: project.pages.map((page) => ({ ...page, nodes: [] })),
  };
}
export function requestedDynamicSources(project: Project, partId?: string) {
  if (!project.dynamicSources?.length) return [];
  const part = project.parts.find((item) => item.id === partId);
  const ids = new Set(
    effectiveFields(project, partId)
      .filter(
        (field) =>
          !field.autoGenerated &&
          field.source.kind === "dynamic" &&
          !(
            part &&
            Object.hasOwn(part.systemValues, field.id) &&
            field.source.dynamicMode !== "choices"
          ),
      )
      .map((field) => field.source.dynamicSourceId)
      .filter(Boolean),
  );
  return (project.dynamicSources || []).filter((source) => ids.has(source.id));
}
export function dynamicSourceCanSkip(
  project: Project,
  sourceId: string,
  partId?: string,
): boolean {
  const policies = project.parts.find(
    (part) => part.id === partId,
  )?.dynamicSourcePolicies;
  const policy =
    policies && Object.hasOwn(policies, sourceId)
      ? policies[sourceId]
      : undefined;
  return policy
    ? policy === "optional"
    : project.dynamicSources?.find((source) => source.id === sourceId)
        ?.allowSkip === true;
}
export function resolveSourceParameters(
  project: Project,
  raw?: unknown,
  partId?: string,
): SourceParameters | undefined {
  let input: Record<string, Record<string, unknown> | null>;
  try {
    input =
      parseContext({ sourceParameters: raw === undefined ? {} : raw })
        .sourceParameters || {};
  } catch {
    throw new ContextError("CONTEXT_PARAMETERS_INVALID", 422);
  }
  if (
    Object.keys(input).some(
      (id) => !project.dynamicSources?.some((source) => source.id === id),
    )
  )
    throw new ContextError("CONTEXT_PARAMETERS_INVALID", 422);
  const sources = requestedDynamicSources(project, partId);
  if (!sources.length) return undefined;
  const result: SourceParameters = Object.create(null);
  for (const source of sources) {
    const supplied = Object.hasOwn(input, source.id) ? input[source.id] : {};
    if (supplied === null) {
      if (!dynamicSourceCanSkip(project, source.id, partId))
        throw new ContextError("CONTEXT_SOURCE_REQUIRED", 422);
      Object.defineProperty(result, source.id, {
        value: null,
        enumerable: true,
      });
      continue;
    }
    if (
      Object.keys(supplied).some(
        (key) => !source.parameters.some((parameter) => parameter.key === key),
      )
    )
      throw new ContextError("CONTEXT_PARAMETERS_INVALID", 422);
    const validation = validateSubmission(
      dynamicParameterProject(project, source),
      Object.fromEntries(
        Object.entries(supplied).map(([key, value]) => [
          key,
          typeof value === "string" &&
          source.parameters.find((parameter) => parameter.key === key)
            ?.whitespace === "trim"
            ? value.trim()
            : value,
        ]),
      ) as Values,
    );
    if (
      validation.errors.length ||
      Object.values(validation.snapshot).some(
        (value) =>
          !(
            value === null ||
            typeof value === "boolean" ||
            (typeof value === "number" && Number.isFinite(value)) ||
            (typeof value === "string" && value.length <= 1000)
          ),
      )
    )
      throw new ContextError("CONTEXT_PARAMETERS_REQUIRED", 422);
    Object.defineProperty(result, source.id, {
      value: validation.snapshot,
      enumerable: true,
    });
  }
  if (new TextEncoder().encode(JSON.stringify(result)).length > 64000)
    throw new ContextError("CONTEXT_PARAMETERS_LIMIT", 422);
  return result;
}
